/* Demand Vintage — Theme JS */
'use strict';

/* ── Cart state ──────────────────────────────────────── */
const Cart = {
  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },
  async add(variantId, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    return res.json();
  },
  async remove(key) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    return res.json();
  },
  async updateItem(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    return res.json();
  }
};

/* ── Cart Drawer ─────────────────────────────────────── */
class CartDrawer {
  constructor() {
    this.el = document.getElementById('cart-drawer');
    if (!this.el) return;
    this.overlay = this.el.querySelector('.cart-drawer__overlay');
    this.closeBtn = this.el.querySelector('.cart-drawer__close');
    this.body = this.el.querySelector('.cart-drawer__body');
    this.footer = document.getElementById('cart-drawer-footer');
    this.countEls = document.querySelectorAll('.cart-count');

    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });

    this.el.addEventListener('click', e => {
      const btn = e.target.closest('.cart-item__remove');
      if (btn) this.removeItem(btn.dataset.key);
    });

    this.el.addEventListener('change', e => {
      const sel = e.target.closest('.cart-item__qty-select');
      if (sel) this.updateQty(sel.dataset.key, parseInt(sel.value, 10));
    });
  }

  open() {
    this.el.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    this.el.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async refresh() {
    const cart = await Cart.get();
    this.updateCount(cart.item_count);
    this.renderItems(cart);
  }

  updateCount(count) {
    this.countEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  qtyOptions(current) {
    return Array.from({ length: 10 }, (_, i) => i + 1)
      .map(n => `<option value="${n}"${n === current ? ' selected' : ''}>Qty ${n}</option>`)
      .join('');
  }

  renderItems(cart) {
    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your bag is empty.</p>
          <a href="/collections/all" class="btn btn--primary" style="margin-top:2rem;display:inline-block;">Shop Now</a>
        </div>`;
      if (this.footer) this.footer.style.display = 'none';
      return;
    }

    if (this.footer) this.footer.style.display = 'block';

    this.body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__image">
          ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
        </a>
        <div class="cart-item__details">
          <div class="cart-item__meta">
            <p class="cart-item__price">${this.formatMoney(item.final_line_price)}</p>
            <p class="cart-item__name">${item.product_title}</p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          </div>
          <div class="cart-item__actions">
            <div class="cart-item__qty-wrap">
              <select class="cart-item__qty-select" data-key="${item.key}" aria-label="Quantity">
                ${this.qtyOptions(item.quantity)}
              </select>
              <svg class="cart-item__qty-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
            </button>
          </div>
        </div>
      </div>
    `).join('');

    const totalEl = document.getElementById('cart-drawer-total');
    if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price);
  }

  async removeItem(key) {
    await Cart.remove(key);
    this.refresh();
  }

  async updateQty(key, qty) {
    await Cart.updateItem(key, qty);
    this.refresh();
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-GB', { style: 'currency', currency: window.shopCurrency || 'GBP' });
  }
}

/* ── Mobile Nav ──────────────────────────────────────── */
class MobileNav {
  constructor() {
    this.el = document.getElementById('mobile-nav');
    if (!this.el) return;
    this.overlay = this.el.querySelector('.mobile-nav__overlay');
    this.closeBtn = this.el.querySelector('.mobile-nav__close');
    this.toggleBtn = document.getElementById('menu-toggle');

    this.toggleBtn?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
  }

  open() { this.el.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
  close() { this.el.classList.remove('is-open'); document.body.style.overflow = ''; }
}

/* ── Search Drawer ───────────────────────────────────── */
class SearchDrawer {
  constructor() {
    this.el = document.getElementById('search-drawer');
    if (!this.el) return;
    this.overlay = this.el.querySelector('.search-drawer__overlay');
    this.closeBtn = this.el.querySelector('.search-drawer__close');
    this.input = this.el.querySelector('.search-drawer__input');
    const openBtn = document.getElementById('search-toggle');

    openBtn?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
  }

  open() { this.el.classList.add('is-open'); this.input?.focus(); }
  close() { this.el.classList.remove('is-open'); }
}

/* ── Product Gallery ─────────────────────────────────── */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.mainImg = el.querySelector('.product-gallery__main img');
    this.thumbs = el.querySelectorAll('.product-gallery__thumb');
    if (!this.mainImg || !this.thumbs.length) return;
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.setActive(i)));
  }

  setActive(index) {
    const thumb = this.thumbs[index];
    const src = thumb.dataset.src;
    const srcset = thumb.dataset.srcset;
    if (src) this.mainImg.src = src;
    if (srcset) this.mainImg.srcset = srcset;
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }
}

/* ── Variant Selector ────────────────────────────────── */
class VariantSelector {
  constructor(form) {
    this.form = form;
    try {
      const jsonEl = document.getElementById('product-json-data');
      this.productData = jsonEl ? JSON.parse(jsonEl.textContent) : {};
    } catch (e) {
      this.productData = {};
    }
    try {
      const invEl = document.getElementById('product-inventory-data');
      this.inventoryData = invEl ? JSON.parse(invEl.textContent) : {};
    } catch (e) {
      this.inventoryData = {};
    }
    this.priceEl = document.querySelector('.price__current');
    this.comparePriceEl = document.querySelector('.price__compare');
    this.atcBtn = form.querySelector('.product-form__atc');
    this.stockBar = document.getElementById('product-stock-bar');
    this.swatches = form.querySelectorAll('.swatch-btn');

    this.swatches.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-sold-out')) return;
        const group = btn.closest('.swatch-grid');
        group.querySelectorAll('.swatch-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.updateVariant();
      });
    });
  }

  getSelectedOptions() {
    const opts = [];
    this.form.querySelectorAll('.swatch-grid').forEach(grid => {
      const active = grid.querySelector('.swatch-btn.is-active');
      if (active) opts.push(active.dataset.value);
    });
    return opts;
  }

  findVariant(options) {
    return (this.productData.variants || []).find(v =>
      v.options.every((o, i) => o === options[i])
    );
  }

  updateVariant() {
    const options = this.getSelectedOptions();
    const variant = this.findVariant(options);
    if (!variant) return;

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Update hidden variant input
    const input = this.form.querySelector('input[name="id"]');
    if (input) input.value = variant.id;

    // Update price
    if (this.priceEl) {
      const price = (variant.price / 100).toLocaleString('en-GB', { style: 'currency', currency: window.shopCurrency || 'GBP' });
      this.priceEl.textContent = price;
    }
    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const cp = (variant.compare_at_price / 100).toLocaleString('en-GB', { style: 'currency', currency: window.shopCurrency || 'GBP' });
        this.comparePriceEl.textContent = cp;
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    // Update ATC button
    if (this.atcBtn) {
      if (variant.available) {
        this.atcBtn.disabled = false;
        this.atcBtn.textContent = 'Add to Bag';
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'Sold Out';
      }
    }

    // Update inventory bar
    if (this.stockBar) {
      const invData = this.inventoryData[variant.id] || {};
      const inv = invData.inventory_quantity ?? 0;
      const tracked = invData.inventory_management === 'shopify';
      let html = '';
      if (variant.available) {
        if (tracked && inv <= 5) {
          html = `<span class="product-stock product-stock--urgent">Only ${inv} left — selling fast</span>`;
        } else if (tracked && inv <= 15) {
          html = `<span class="product-stock product-stock--low">Low stock — ${inv} remaining</span>`;
        } else {
          html = `<span class="product-stock product-stock--available">&#10003; In Stock</span>`;
        }
      } else {
        html = `<span class="product-stock product-stock--out">Out of Stock</span>`;
      }
      this.stockBar.innerHTML = html;
    }
  }
}

/* ── Add to Cart form ────────────────────────────────── */
class AddToCartForm {
  constructor(form, cartDrawer) {
    this.form = form;
    this.cartDrawer = cartDrawer;
    this.btn = form.querySelector('.product-form__atc');
    form.addEventListener('submit', e => this.onSubmit(e));
  }

  async onSubmit(e) {
    e.preventDefault();
    const id = this.form.querySelector('input[name="id"]')?.value;
    if (!id) return;
    const originalText = this.btn.textContent;
    this.btn.textContent = 'Adding…';
    this.btn.disabled = true;
    try {
      await Cart.add(id);
      this.btn.textContent = 'Added!';
      setTimeout(() => {
        this.btn.textContent = originalText;
        this.btn.disabled = false;
      }, 1200);
      this.cartDrawer?.open();
    } catch (err) {
      this.btn.textContent = 'Error — try again';
      setTimeout(() => { this.btn.textContent = originalText; this.btn.disabled = false; }, 2000);
    }
  }
}

/* ── Accordion ───────────────────────────────────────── */
function initAccordions() {
  document.querySelectorAll('.accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const body = trigger.nextElementSibling;
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isOpen);
      body.classList.toggle('is-open', !isOpen);
    });
  });
}

/* ── Nav dropdowns ───────────────────────────────────── */
function initNavDropdowns() {
  document.querySelectorAll('.site-nav__item--has-dropdown').forEach(item => {
    let closeTimer;
    item.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      item.classList.add('is-open');
    });
    item.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => item.classList.remove('is-open'), 150);
    });
  });
}

/* ── Cart toggle ─────────────────────────────────────── */
function initCartToggle(cartDrawer) {
  document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
    btn.addEventListener('click', e => { e.preventDefault(); cartDrawer?.open(); });
  });
}

/* ── Init ────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = new CartDrawer();
  new MobileNav();
  new SearchDrawer();

  const gallery = document.querySelector('.product-gallery');
  if (gallery) new ProductGallery(gallery);

  const productForm = document.querySelector('.product-form');
  if (productForm) {
    new VariantSelector(productForm);
    new AddToCartForm(productForm, cartDrawer);
  }

  initAccordions();
  initCartToggle(cartDrawer);
  initNavDropdowns();

  // Init cart count on load
  Cart.get().then(cart => {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
  });
});
